import { tutorialVideos } from '@lib';
import clsx from 'clsx';
import React from 'react';

import { Icon } from '../../Icon/Icon';
import styles from '../QuickStart.module.scss';

interface VideoStepProps {
  step: number;
  onStepSelect: (i: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onFinish: () => void;
}

export const VideoStep: React.FC<VideoStepProps> = ({ step, onStepSelect, onPrev, onNext, onFinish }) => {
  const current = tutorialVideos[step];
  const isFirst = step === 0;
  const isLast = step === tutorialVideos.length - 1;

  return (
    <>
      <div className={styles.header}>
        <div>
          <div className={styles.eyebrow}>
            Calvin Tour · Step {step + 1} of {tutorialVideos.length}
          </div>
          <div className={styles.title}>{current.title}</div>
        </div>
      </div>

      <div className={styles.progressSection}>
        <div className={styles.dots}>
          {tutorialVideos.map((video, index) => (
            <React.Fragment key={video.id}>
              <button
                onClick={() => onStepSelect(index)}
                title={video.title}
                className={clsx(styles.dot, {
                  [styles.dotDone]: index < step,
                  [styles.dotActive]: index === step,
                })}
              >
                {index < step ? (
                  <Icon
                    name="check"
                    className={styles.dotIcon}
                  />
                ) : (
                  index + 1
                )}
              </button>
              {index < tutorialVideos.length - 1 && (
                <div className={clsx(styles.line, { [styles.lineDone]: index < step })} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className={styles.progressMeta}>
          {current.desc && <div className={styles.desc}>{current.desc}</div>}
          {current.duration && (
            <div className={styles.durationBadge}>
              <Icon
                name="clock"
                className={styles.durationIcon}
              />
              <span>{current.duration}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.videoSection}>
        <button
          className={styles.arrowBtn}
          onClick={onPrev}
          disabled={isFirst}
        >
          <Icon
            name="angle-right"
            className={styles.arrowIconFlipped}
          />
        </button>
        <div className={styles.videoWrapper}>
          {current.vimeoId ? (
            <iframe
              src={`https://player.vimeo.com/video/${current.vimeoId}?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479`}
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              title={current.title}
            />
          ) : (
            <div className={styles.comingSoon}>Coming soon</div>
          )}
        </div>
        <button
          className={styles.arrowBtn}
          onClick={onNext}
          disabled={isLast}
        >
          <Icon
            name="angle-right"
            className={styles.arrowIcon}
          />
        </button>
      </div>

      <div className={styles.footer}>
        <div className={styles.pips}>
          {tutorialVideos.map((video, index) => (
            <span
              key={video.id}
              className={clsx(styles.pip, {
                [styles.pipActive]: index === step,
                [styles.pipDone]: index < step,
              })}
            />
          ))}
        </div>
        <div className={styles.footerButtons}>
          <button
            className={styles.backBtn}
            onClick={onPrev}
            disabled={isFirst}
          >
            Back
          </button>
          <button
            className={styles.nextBtn}
            onClick={isLast ? onFinish : onNext}
          >
            {isLast ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </>
  );
};
